import React, { useState, useEffect } from 'react';
import config from '../../config';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Table as MuiTable,
} from '@mui/material';
import { motion } from 'framer-motion';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import * as XLSX from 'xlsx';
import { useRoomNames } from './useRoomNames';
import { toast } from 'react-toastify';

export default function InvoiceList() {
  const { roomLabel } = useRoomNames();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const Companycurrency = company?.currency ?? '';
  const token = sessionStorage.getItem('token');

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const url = `${config.API_BASE_URL}/home/GetallInvoice?roomNumber=${roomNumber}&fromdate=${fromDate}&todate=${toDate}&id=${company?.id}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const text = await res.text();

      if (text) {
        const data = JSON.parse(text);
        console.log('✅ Invoice API Result:', data);
        setReport(data);
      } else {
        console.log('⚠️ Empty response received');
        setReport(null);
      }
    } catch (err) {
      console.error('❌ Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const filteredList =
    report?.hotelBookingLists?.filter((inv) =>
      `${inv.firstName} ${inv.lastName}`
        .toLowerCase()
        .includes(searchText.toLowerCase()),
    ) || [];

  // ────────────────────────────────────────────────
  // Excel Export – now matches the visible table columns + extras
  // ────────────────────────────────────────────────
  const exportToExcel = () => {
    if (!filteredList || filteredList.length === 0) {
      toast.error('No data to export!');
      return;
    }

    const formattedData = filteredList.map((inv, index) => ({
      '#': index + 1,
      'Invoice Number': ` INV26_${inv.invoiceNo}`,
      'Guest Name': `${inv.firstName} ${inv.lastName || ''}`,
      'Room No': inv.roomNumbers || '-',
      'Check-In': inv.checkIn
        ? new Date(inv.checkIn).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '-',

      'Check-Out': inv.checkOut
        ? new Date(inv.checkOut).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '-',
      'Booking Source': inv.through || '',
      'Disc. Type': inv.discountType || '',
      'Disc. Value': inv.discountValue || 0,
      'Base Price': inv.baseprice || 0,
      Total: inv.totelPrice || 0,
      'Add On Charge': inv.addonWithGstTotal || 0,
      'Cover Charge': inv.covercharge || 0,
      Voucher: inv.coupen || '',
      'Voucher Amount': inv.couponAmount || 0,
      CGst: inv.cGst || 0,
      SGst: inv.sGst || 0,
      Igst: inv.igst || 0,
      covercgst: inv.covercgst || 0,
      coversgst: inv.coversgst || 0,
      coverigst: inv.coverigst || 0,
      roomChargeCgst: inv.roomChargeCgst || 0,
      roomChargesgst: inv.roomChargesgst || 0,
      'Final + GST': inv.finalpriceWithGST || 0,
      'Booking Id': inv.bookingId,
      Adults: inv.adultCount || 0,
      Children: inv.childCount || 0,
      Mobile: inv.mobileNumber || '',
      Email: inv.email || '',
      Hotel: inv.hotelName || '',
      companyname: inv.companyname || '',
      companyState: inv.companyState || '',
      companyMobile: inv.companyMobile || '',
      companyEmail: inv.companyEmail || '',
      companyCity: inv.companyCity || '',
      companyAddress: inv.companyAddress || '',
      Gstno: inv.gstno || '',
      'Room Type': inv.roomType || '',
      '24 Hour': inv.is24Hour ? 'Yes' : 'No',
      Cancelled: inv.isCancel ? 'Yes' : 'No',
      'Cancel Remark': inv.cancelRemark || '',
      'Coupon Amount': inv.couponAmount || 0, // repeated for clarity
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    // Optional: auto-size columns (better readability)
    const colWidths = [];
    Object.keys(formattedData[0] || {}).forEach((key, i) => {
      let maxLen = key.length;
      formattedData.forEach((row) => {
        const val = String(row[key] || '');
        if (val.length > maxLen) maxLen = val.length;
      });
      colWidths[i] = { wch: Math.min(maxLen + 4, 60) };
    });
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'InvoiceReport');
    XLSX.writeFile(
      workbook,
      `Invoice_Report_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleString('en-GB', { month: 'short' });
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleViewInvoice = (booking) => {
    const baseUrl = window.location.origin;
    window.open(`${baseUrl}/#/invoice/${booking.bookingId}`, '_blank');
  };

  const handleViewDetail = (booking) => {
    setSelectedBooking(booking);
  };

  const handleCloseDetail = () => {
    setSelectedBooking(null);
  };

  const summaryCards = [
    {
      label: 'Total Invoices',
      value: report?.hotelBookingLists?.length || 0,
      color: '#1976d2',
      bg: 'rgba(25,118,210,0.1)',
    },
    {
      label: 'Total GST',
      value: report?.totalgst || 0,
      color: '#d32f2f',
      bg: 'rgba(211,47,47,0.1)',
    },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        variant='h4'
        fontWeight='bold'
        mb={3}
        sx={{ color: '#0a2540', textAlign: 'center' }}>
        🧾 Invoice Summary
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={3}>
          <TextField
            label='Room Number'
            size='small'
            fullWidth
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            type='date'
            label='From Date'
            InputLabelProps={{ shrink: true }}
            size='small'
            fullWidth
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            type='date'
            label='To Date'
            InputLabelProps={{ shrink: true }}
            size='small'
            fullWidth
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            variant='contained'
            fullWidth
            size='large'
            sx={{ height: '40px' }}
            onClick={fetchInvoices}>
            🔍 Search
          </Button>
        </Grid>
      </Grid>

      {loading && (
        <Box
          display='flex'
          justifyContent='center'
          alignItems='center'
          height='40vh'>
          <CircularProgress size={60} />
        </Box>
      )}

      {!loading && !report && (
        <Typography variant='h6' align='center' sx={{ mt: 4 }}>
          No invoices found
        </Typography>
      )}

      {report && (
        <Grid container spacing={3} mb={4}>
          {summaryCards.map((item, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <motion.div whileHover={{ scale: 1.07 }}>
                <Paper
                  elevation={4}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    textAlign: 'center',
                    background: item.bg,
                  }}>
                  <Typography variant='body2'>{item.label}</Typography>
                  <Typography
                    variant='h5'
                    fontWeight='bold'
                    sx={{ color: item.color, mt: 1 }}>
                    {item.label.includes('GST')
                      ? `${Companycurrency}${item.value?.toFixed(2)}`
                      : item.value}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      )}

      <Box display='flex' justifyContent='flex-start' gap={2} mb={3}>
        <TextField
          label='Search Guest'
          size='small'
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ width: 300 }}
        />
        <Button
          variant='outlined'
          startIcon={<FileDownloadIcon />}
          onClick={exportToExcel}
          sx={{ borderRadius: 2, fontWeight: 'bold' }}>
          Export
        </Button>
      </Box>

      {report && (
        <Paper
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
          }}>
          <TableContainer sx={{ maxHeight: 550 }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  {[
                    '#',
                    'InvoiceNo',
                    'Guest Name',
                    'Room No',
                    'CheckIn',
                    'CheckOut',
                    'Booking Source',
                    'Disc. Type',
                    'Disc. Val',
                    'Price',
                    'Total',
                    'Add On Charge',
                    'Covercharge',
                    'Voucher',
                    'Voucher Amount',
                    'CGST',
                    'SGST',
                    'IGST',
                    'Covercgst',
                    'Coversgst',
                    'Coverigst',
                    'RoomChargeCgst',
                    'RoomChargesgst',
                    'With GST',
                    '',
                    'Details',
                  ].map((head, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        backgroundColor: '#0a2540',
                        color: '#fff',
                        textAlign:
                          head === 'Price' ||
                          head === 'Total' ||
                          head === 'Add On Charge' ||
                          head === 'Covercharge' ||
                          head === 'Voucher Amount' ||
                          head === 'With GST'
                            ? 'right'
                            : 'center',
                        border: '1px solid #444',
                        fontWeight: 600,
                        py: 1.5,
                      }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={17}
                      align='center'
                      sx={{ py: 4, border: '1px solid #ddd' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredList.map((inv, i) => (
                    <TableRow key={i} hover>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {i + 1}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        {' '}
                        INV26_{inv.invoiceNo}{' '}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.firstName} {inv.lastName || ''}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.roomNumbers ? String(inv.roomNumbers).split(',').map((n) => roomLabel(n.trim())).join(', ') : '-'}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.checkIn ? formatDate(inv.checkIn) : '-'}
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.checkOut ? formatDate(inv.checkOut) : '-'}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.through}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.discountType}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.discountValue}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.baseprice || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.totelPrice || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.addonWithGstTotal || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.covercharge || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {inv.coupen}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.couponAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.cGst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.sGst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.igst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.covercgst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.coversgst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.coverigst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.roomChargeCgst || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.roomChargesgst || 0).toFixed(2)}
                      </TableCell>

                      <TableCell
                        align='right'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        {Companycurrency}
                        {(inv.finalpriceWithGST || 0).toFixed(2)}
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        <Button
                          variant='contained'
                          size='small'
                          color='success'
                          onClick={() => handleViewInvoice(inv)}>
                          View Invoice
                        </Button>
                      </TableCell>
                      <TableCell
                        align='center'
                        sx={{ border: '1px solid #e0e0e0' }}>
                        <Button
                          variant='outlined'
                          size='small'
                          color='primary'
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDetail(inv)}>
                          View Detail
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* View Detail Modal */}
      <Dialog
        open={!!selectedBooking}
        onClose={handleCloseDetail}
        maxWidth='sm'
        fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0a2540', color: 'white' }}>
          Guest Details – Booking #{selectedBooking?.bookingId}
        </DialogTitle>

        <DialogContent dividers>
          {selectedBooking && (
            <MuiTable size='small'>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Guest Name</TableCell>
                  <TableCell>
                    {selectedBooking.firstName} {selectedBooking.lastName || ''}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Mobile</TableCell>
                  <TableCell>{selectedBooking.mobileNumber || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>{selectedBooking.email || '-'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Adults</TableCell>
                  <TableCell>{selectedBooking.adultCount}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Children</TableCell>
                  <TableCell>{selectedBooking.childCount}</TableCell>
                </TableRow>
              </TableBody>
            </MuiTable>
          )}
        </DialogContent>

        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={handleCloseDetail} variant='outlined'>
            Close
          </Button>
          {selectedBooking && (
            <Button
              variant='contained'
              color='success'
              onClick={() => handleViewInvoice(selectedBooking)}>
              Open Invoice
            </Button>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
